/* import __COLOCATED_TEMPLATE__ from './trends-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { HEIGHT_TYPES, WIDTH_TYPES } from 'embercom/models/reporting/custom/chart';
import { VISUALIZATION_TYPES } from 'embercom/models/reporting/custom/visualization-options';
import { metricConfigs } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topics-explorer/common';
import { volumeMetricConfig } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/common';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import MetricPickerItem from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topics-explorer/metric-picker-item';
import type { LogicalFilter } from 'embercom/components/reporting/custom/filters';
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import { insightsMetrics } from '../../../ai-insights/fin-insights/jobs';
import PALETTE from '@intercom/pulse/lib/palette';
import type Range from 'embercom/models/reporting/range';
import moment from 'moment-timezone';

interface Args {
  trendingTopic?: TrendingTopic;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  dateRange?: Range;
  isDateFilterDisabled?: boolean;
  showMetricSelect?: boolean;
}

export default class TrendsPanel extends Component<Args> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  get selectedMetricId(): string {
    let primaryMetric = this.args.trendingTopic?.primaryMetric;
    if (primaryMetric) {
      return primaryMetric;
    }
    return Object.keys(this.metricConfigs)[0];
  }

  /**
   * Unique key that changes when the trending topic or selected metric changes.
   * Used in the template to force the chart to re-render.
   */
  get chartRenderKey(): string {
    let topic = this.args.trendingTopic;
    let topicKey = topic ? `${topic.topicName}-${topic.subtopicName}-${topic.weekStart}` : 'none';
    return `${topicKey}-${this.selectedMetricId}`;
  }

  get dateRangeConfig() {
    if (!this.args.dateRange) {
      return {
        selection: 'past_4_weeks',
      };
    }

    return {
      selection: 'custom',
      start: this.args.dateRange.start,
      end: this.args.dateRange.end,
    };
  }

  get serializedReport() {
    return {
      id: 'trends-panel',
      title: null,
      description: null,
      charts: [
        {
          id: 'combo',
          title: null,
          description: null,
          show_default_chart_title: false,
          visualization_type: VISUALIZATION_TYPES.COLUMN_LINE,
          width_type: WIDTH_TYPES.FULL,
          height_type: HEIGHT_TYPES.TALL,
          view_by: 'time',
          view_by_time_interval: 'week',
          disable_drill_in_popover: false,
          chart_series: [
            {
              metric_id: insightsMetrics(this.intl).volume.metric_id,
              filters: this.seriesFilters,
              color: PALETTE['vis-azure-50'],
              label: this.intl.t('ai-agent.analyze.performance.new-conversations'),
            },
            {
              ...this.selectedMetricConfig.seriesConfig,
              filters: this.seriesFilters,
              color: this.selectedMetricConfig.seriesConfig.color,
              label: this.selectedMetricConfig.label,
            },
          ],
        },
      ],
      date_range: this.dateRangeConfig,
    };
  }

  private get appTimezone() {
    return this.appService.app.timezone;
  }

  get seriesFilters(): LogicalFilter {
    if (!this.args.trendingTopic) {
      return {
        type: 'and',
        filters: [],
      };
    }

    let weekStartMoment = moment.utc(this.args.trendingTopic.weekStart).tz(this.appTimezone, true);
    let weekStart = weekStartMoment.clone().startOf('isoWeek');
    let weekEnd = weekStartMoment.clone().endOf('isoWeek');
    let startDate = weekStart.clone().subtract(11, 'weeks').startOf('day');

    return {
      type: 'and',
      filters: [
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_topic',
            property: 'conversation_custom_fields#ai_insights_topic',
            values: [this.args.trendingTopic.topicName],
          },
        },
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_subtopic',
            property: 'conversation_custom_fields#ai_insights_subtopic',
            values: [this.args.trendingTopic.subtopicName],
          },
        },
        {
          type: 'date_range',
          data: {
            attribute: 'conversation.first_closed_at',
            property: 'first_closed_at',
            values: [startDate.valueOf(), weekEnd.valueOf()],
          },
        },
      ],
    };
  }

  get selectedMetricConfig() {
    if (this.selectedMetricId === 'v1.new_conversations') {
      return volumeMetricConfig(this.intl);
    }

    return this.metricConfigs[this.selectedMetricId];
  }

  @cached
  get metricConfigs() {
    return metricConfigs(this.intl);
  }

  get metricOptions() {
    return Object.entries(this.metricConfigs).map(([metricId, metricConfig]) => ({
      text: metricConfig.longLabel ?? metricConfig.label,
      value: metricId,
      tooltipText: metricConfig.tooltip,
      component: MetricPickerItem,
      componentAttrs: { color: metricConfig.metricPickerItemClass },
    }));
  }

  @action
  changeMetric(metricId: string) {
    this.args.trackAnalyticsEvent('selected', 'trends-metric-select', {
      topic: this.args.trendingTopic?.topicName,
      subtopic: this.args.trendingTopic?.subtopicName,
      week_start: this.args.trendingTopic?.weekStart,
      metric: metricId,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends::TrendsPanel': typeof TrendsPanel;
  }
}
